import dayjsTranslation from 'dayjs';
import type { AnyAction } from 'redux';
import { imagerySupportedSources } from '../../screens/Map/components/Overlays/ScenePicker/common/FilterByPopover/components/FilterByPopoverSourceSelector/constants';
import actions from './actions';
import type { ImageAssetState } from './models';
import { adapter } from './models';

const initialState: ImageAssetState = adapter.getInitialState({
  status: 'loading',
  page: 1,
  loadAttempts: 0,
  selectedSceneGroupId: null,
  selectedSideSceneGroupId: null,
  selectedImages: [],
  selectedSideImages: [],
  compositionStatus: 'success',
  sideCompositionStatus: 'success',
  fieldsWithPlanet: [],
  error: null,
  dateRange: [dayjsTranslation().subtract(60, 'day').toDate(), dayjsTranslation().toDate()],
  dateRangeReport: [dayjsTranslation().subtract(1, 'month').toDate(), dayjsTranslation().toDate()],
  options: {
    cloudCoverage: 1,
    sources: imagerySupportedSources,
    cloudOptions: [],
  },
  optionsSide: {
    cloudCoverage: 1,
    sources: imagerySupportedSources,
    cloudOptions: [],
  },
  viewMode: 'list',
  assetsCursor: null,
  timeToLoad: null,
  droneRequestStatus: `idle`,
  showDroneImages: false,
  unavailableSentinelImageDates: [],
  s2bImageExclusionEnabled: false,
});

const reducer = (state = initialState, action: AnyAction): ImageAssetState => {
  switch (action.type) {
    case actions.SET_S2B_IMAGE_EXCLUSION_ENABLED:
      return {
        ...state,
        s2bImageExclusionEnabled: action.payload,
      };
    case actions.LOAD_ASSETS.started().type:
      return adapter.removeAll({
        ...state,
        status: 'loading',
        compositionStatus: 'success',
        sideCompositionStatus: 'success',
        sceneCancelToken: action.payload.cancelToken,
        loadAttempts: action.payload.newloadAttempts,
        timeToLoad: null,
        unavailableSentinelImageDates: [],
      });
    case actions.LOAD_ASSETS.success().type:
      return adapter.upsertMany(action.payload.data, {
        ...state,
        assetsCursor: action.payload.cursor,
        timeToLoad: action.payload.timeToLoad,
        status: 'success',
        loadAttempts: 0,
        unavailableSentinelImageDates: action.payload.unavailableSentinelImageDates ?? [],
      });
    case actions.LOAD_ASSETS.error().type:
      return {
        ...state,
        status: 'error',
      };
    case actions.LOAD_DRONE_ASSETS.started().type:
      return {
        ...state,
        droneRequestStatus: 'loading',
      };
    case actions.LOAD_DRONE_ASSETS.success().type:
      return adapter.upsertMany(action.payload.data, {
        ...state,
        droneRequestStatus: 'success',
      });
    case actions.LOAD_DRONE_ASSETS.error().type:
      return {
        ...state,
        droneRequestStatus: 'error',
      };
    case actions.SET_DRONE_STATUS:
      return {
        ...state,
        showDroneImages: action.payload,
      };
    case actions.LOAD_COMPOSITION.started().type:
      return {
        ...state,
        compositionStatus: 'loading',
      };
    case actions.LOAD_COMPOSITION.success().type:
      return {
        ...state,
        compositionStatus: 'success',
      };
    case actions.LOAD_COMPOSITION.error().type:
      return {
        ...state,
        compositionStatus: 'error',
      };
    case actions.LOAD_SIDE_COMPOSITION.started().type:
      return {
        ...state,
        sideCompositionStatus: 'loading',
      };
    case actions.LOAD_SIDE_COMPOSITION.success().type:
      return {
        ...state,
        sideCompositionStatus: 'success',
      };
    case actions.LOAD_SIDE_COMPOSITION.error().type:
      return {
        ...state,
        sideCompositionStatus: 'error',
      };
    case actions.SET_SELECTED_SCENE_GROUP_ID:
      return {
        ...state,
        selectedSceneGroupId: action.payload,
      };
    case actions.SET_SELECTED_IMAGES:
      return {
        ...state,
        selectedImages: action.payload,
      };
    case actions.SET_SIDE_SELECTED_SCENE_GROUP_ID:
      return {
        ...state,
        selectedSideSceneGroupId: action.payload,
      };
    case actions.SET_SIDE_SELECTED_IMAGES:
      return {
        ...state,
        selectedSideImages: action.payload,
      };
    case actions.SET_CATALOG_OPTIONS:
      return {
        ...state,
        options: action.payload,
      };
    case actions.SET_CATALOG_OPTIONS_SIDE:
      return {
        ...state,
        optionsSide: action.payload,
      };
    case actions.SET_DATE_RANGE:
      return {
        ...state,
        dateRange: action.payload,
      };
    case actions.SET_DATE_RANGE_REPORT:
      return {
        ...state,
        dateRangeReport: action.payload,
      };
    case actions.SET_IMAGE_ASSETS_PAGE:
      return {
        ...state,
        page: action.payload,
      };
    case actions.SET_ASSETS_CURSOR:
      return {
        ...state,
        assetsCursor: action.payload,
      };
    case actions.UPDATE_ASSET_GROUP_COMPOSITION:
      return adapter.updateOne(
        {
          id: action.payload.id,
          changes: {
            images: [action.payload.referenceImage],
            referenceImage: action.payload.referenceImage,
          },
        },
        {
          ...state,
          selectedImages: action.payload.isSide
            ? state.selectedImages
            : [action.payload.referenceImage],
          selectedSideImages: action.payload.isSide
            ? [action.payload.referenceImage]
            : state.selectedSideImages,
        }
      );
    case actions.RESET: {
      const removedAll = adapter.removeAll(state);
      return {
        ...removedAll,
        assetsCursor: null,
        timeToLoad: null,
        selectedSceneGroupId: null,
        selectedSideSceneGroupId: null,
        selectedImages: [],
        selectedSideImages: [],
        unavailableSentinelImageDates: [],
        compositionStatus: 'success',
        sideCompositionStatus: 'success',
      };
    }
    default:
      return state;
  }
};

const { selectAll } = adapter.getSelectors();

export const selectAllSceneGroups = selectAll;

export default reducer;
